import { useState, useEffect, useMemo } from 'react';
import { useGuru } from '../context/GuruContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export interface ShiftConfig {
    active: boolean;
    start: string;
    end: string;
    tolerance: number;
    presensiAwal: number;
    presensiAkhir: number;
    name?: string; // e.g. "Shift Pagi"
}

interface GrupPresensiData {
    id: string;
    nama: string;
    shift_pagi: ShiftConfig;
    shift_siang: ShiftConfig;
    shift_malam: ShiftConfig;
    hari_kerja?: string[]; // Array of active days e.g. ["Senin", "Selasa", ...]
    custom_daily_shifts?: Record<string, {
        shift_pagi?: ShiftConfig;
        shift_siang?: ShiftConfig;
        shift_malam?: ShiftConfig;
    }>;
}

interface PersonalConfig {
    custom_daily_shifts?: Record<string, {
        shift_pagi?: ShiftConfig;
        shift_siang?: ShiftConfig;
        shift_malam?: ShiftConfig;
    }>;
}

const DAYS_MAP = ['Ahad', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

export const useUserShift = (authUser: any) => {
    const { guruRecords } = useGuru();
    const [shiftData, setShiftData] = useState<{
        todayShift: string | null;
        fullSchedule: Record<string, string>;
        isMultiple: boolean;
        todayConfig: ShiftConfig | null;
        activeShifts: ShiftConfig[]; // New: List of all active shifts for today
    }>({
        todayShift: null,
        fullSchedule: {},
        isMultiple: false,
        todayConfig: null,
        activeShifts: []
    });
    const [loading, setLoading] = useState(true);

    const currentUser = useMemo(() => {
        if (!authUser?.email) return null;
        return guruRecords.find(g => g.email === authUser.email);
    }, [authUser, guruRecords]);

    useEffect(() => {
        const fetchShift = async () => {
            if (!currentUser || !currentUser.groupPresensiId) {
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch Group Data
                const groupRef = doc(db, 'grup_presensi', currentUser.groupPresensiId);
                const groupSnap = await getDoc(groupRef);

                if (!groupSnap.exists()) {
                    setLoading(false);
                    return;
                }

                const groupData = groupSnap.data() as GrupPresensiData;

                // 2. Fetch Personal Override (if any)
                const memberRef = doc(db, 'grup_presensi', currentUser.groupPresensiId, 'anggota', currentUser.id);
                const memberSnap = await getDoc(memberRef);
                const memberData = memberSnap.exists() ? memberSnap.data() : null;
                const personalConfig = memberData?.personal_config as PersonalConfig | undefined;

                // 3. Determine Schedule
                const schedule: Record<string, string> = {};
                const configs: Record<string, ShiftConfig> = {};

                // For "today's active shifts" list
                const todayIndex = new Date().getDay();
                const todayName = DAYS_MAP[todayIndex];
                const todayActiveShifts: ShiftConfig[] = [];

                const days = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Ahad'];

                days.forEach(day => {
                    // Iterate all potential shift types to build daily schedule
                    const shiftTypes: Array<'shift_pagi' | 'shift_siang' | 'shift_malam'> = ['shift_pagi', 'shift_siang', 'shift_malam'];

                    const activeConfigsForDay: ShiftConfig[] = [];

                    shiftTypes.forEach(type => {
                        let config = { ...groupData[type], name: type === 'shift_pagi' ? 'Shift Pagi' : type === 'shift_siang' ? 'Shift Siang' : 'Shift Malam' };

                        // Check "hari_kerja" field. If exists, and day is not in it, default to INACTIVE.
                        if (groupData.hari_kerja && Array.isArray(groupData.hari_kerja)) {
                            // Normalize check for Ahad/Minggu
                            const isWorkDay = groupData.hari_kerja.includes(day) || (day === 'Ahad' && groupData.hari_kerja.includes('Minggu'));
                            if (!isWorkDay) {
                                config.active = false;
                            }
                        }

                        // Group Override
                        // Check for 'Ahad' or 'Minggu' if day is Ahad
                        let groupOverride = groupData.custom_daily_shifts?.[day]?.[type];
                        if (!groupOverride && day === 'Ahad') {
                            groupOverride = groupData.custom_daily_shifts?.['Minggu']?.[type];
                        }

                        if (groupOverride) {
                            if (groupOverride.active === false) { // Explicitly false check
                                config.active = false;
                            } else {
                                config = { ...config, ...groupOverride };
                            }
                        }

                        // Personal Override
                        let personalOverride = personalConfig?.custom_daily_shifts?.[day]?.[type];
                        if (!personalOverride && day === 'Ahad') {
                            personalOverride = personalConfig?.custom_daily_shifts?.['Minggu']?.[type];
                        }

                        if (personalOverride) {
                            if (personalOverride.active === false) {
                                config.active = false;
                            } else {
                                config = { ...config, ...personalOverride };
                            }
                        }

                        if (config.active && config.start && config.end) {
                            activeConfigsForDay.push(config);
                        }
                    });

                    // Build string for calendar
                    if (activeConfigsForDay.length > 0) {
                        schedule[day] = activeConfigsForDay.map(c => `${c.start} - ${c.end}`).join(' / ');
                        // Keep the first one as primary config for backward compatibility, or intelligent selection?
                        // For legacy 'configs[day]', we just take the first one.
                        configs[day] = activeConfigsForDay[0];

                        if (day === todayName) {
                            todayActiveShifts.push(...activeConfigsForDay);
                        }
                    }
                });

                setShiftData({
                    todayShift: schedule[todayName] || null,
                    fullSchedule: schedule,
                    isMultiple: Object.keys(schedule).length > 1,
                    todayConfig: configs[todayName] || null,
                    activeShifts: todayActiveShifts
                });

            } catch (error) {
                console.error("Error fetching shift data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchShift();
    }, [currentUser]);

    return { shiftData, loading };
};
